import React from "react";

import { StyledEngineProvider } from "@mui/material/styles";
import { WeezSocket } from "@weezevent/piglet";
import PropTypes from "prop-types";
import { QueryClient, QueryClientProvider } from "react-query";
import { useParams } from "react-router-dom";

import WeezPayAPIProvider from "api/components/WeezPayAPIProvider";
import OrganizationIdContextProvider from "contexts/OrganizationIdContextProvider";
import WidgetUUIDContextProvider from "contexts/WidgetUUIDContextProvider";
import useForwardIframeHeight from "hooks/useForwardIframeHeight";
import { AuthenticationProvider } from "libs/authentication";
import { MessageProvider } from "libs/message";
import Widget from "routers/Widget";

const getRootSize = (widgetContainer) => {
  // Get root div
  const widgetContainerSettings = widgetContainer.getBoundingClientRect();
  return Math.ceil(widgetContainerSettings.height);
};

const resizeEvent = () => {
  const widgetContainer = document.getElementById("widget-container");
  if (!widgetContainer) {
    return;
  }
  const height = getRootSize(widgetContainer);
  WeezSocket.send({ type: "resize", origin: "gadget", payload: { height } });
};

WeezSocket.listen(); // piglet will answer to the messages (for example send us the parent url)
setInterval(resizeEvent, 200);

const queryClient = new QueryClient();

/**
 * App router page.
 * This is the first entrypoint of the application.
 * For now, it's a "sub entrypoint" of the new gadget, included in a Route of the old gadget router.
 */
const AppWidget = ({ locales = null, theme = null }) => {
  const { organizationId, widgetUUID } = useParams();
  useForwardIframeHeight(widgetUUID);

  return (
    <QueryClientProvider client={queryClient}>
      <OrganizationIdContextProvider>
        <WidgetUUIDContextProvider>
          <AuthenticationProvider organizationId={organizationId.replace("O", "")} widgetUUID={widgetUUID}>
            <MessageProvider defaultClearingTimeout={4000}>
              <StyledEngineProvider injectFirst>
                <WeezPayAPIProvider>
                  <Widget locales={locales} theme={theme} widgetUUID={widgetUUID} />
                </WeezPayAPIProvider>
              </StyledEngineProvider>
            </MessageProvider>
          </AuthenticationProvider>
        </WidgetUUIDContextProvider>
      </OrganizationIdContextProvider>
    </QueryClientProvider>
  );
};

AppWidget.propTypes = {
  /**
   * The translations to override in the widget.
   */
  locales: PropTypes.object,
  /**
   * The theme to inject and use in the widget.
   */
  theme: PropTypes.object,
};

export default AppWidget;
